/**
 * 뷰(모달/팝업) 스택 — push 순서대로 depth를 자동 부여해 "위에 쌓임"을 보장한다 (Phaser 비의존).
 *
 * 뷰 계약은 구조적 `StackableView`(setDepth + close)만 — Phaser Container든 plain 객체든
 * 만족하면 쌓을 수 있다 (DIP: 패키지가 인터페이스 정의, 앱 뷰가 충족). node 단위테스트 가능.
 *
 * depth 계산은 `depthFor(index)` 주입 — 보통 `DepthBands.depthFor(band, i)`를 바인딩한다.
 * 중간 뷰 제거 시 잔여 뷰들의 depth를 index 순으로 재부여해 상대 순서를 보존한다.
 *
 * ⚠ 닫힘 동기화: `close()`가 비동기(퇴장 tween 후 destroy)인 뷰는 호스트가 닫힘 이벤트
 * (예: ModalLayer `MODAL_EVENT.Closed`)에서 `remove(view)`를 호출해 스택과 동기화한다.
 * `remove`는 idempotent — closeAll/closeByTag 경로와 이벤트 경로가 중복 호출해도 안전.
 */
export interface StackableView {
  setDepth(depth: number): unknown;
  close(): void;
}

export interface ViewStackOptions {
  /** 스택 index(0=바닥) → 절대 depth. 보통 DepthBands 밴드에 바인딩. */
  readonly depthFor: (index: number) => number;
}

interface Entry<V> {
  readonly view: V;
  readonly tag?: string;
}

export class ViewStack<V extends StackableView = StackableView> {
  private entries: Entry<V>[] = [];

  constructor(private readonly options: ViewStackOptions) {}

  get size(): number {
    return this.entries.length;
  }

  has(view: V): boolean {
    return this.entries.some((e) => e.view === view);
  }

  top(): V | null {
    return this.entries.length > 0 ? this.entries[this.entries.length - 1].view : null;
  }

  /**
   * 뷰를 스택 맨 위에 올리고 depth 자동 부여. 이미 스택에 있으면 맨 위로 이동만 (재push 안전).
   * tag는 closeByTag 그룹 닫기용 (예: "overlay" = 탭 전환 시 일괄 정리 대상).
   */
  push(view: V, opts?: { tag?: string }): V {
    const existing = this.entries.findIndex((e) => e.view === view);
    if (existing >= 0) {
      this.bringToTop(view);
      return view;
    }
    this.entries.push({ view, tag: opts?.tag });
    view.setDepth(this.options.depthFor(this.entries.length - 1));
    return view;
  }

  /** 스택에서 제거(close 호출 없음) — 닫힘 이벤트 핸들러용. idempotent. 잔여 depth 재부여. */
  remove(view: V): boolean {
    const idx = this.entries.findIndex((e) => e.view === view);
    if (idx < 0) return false;
    this.entries.splice(idx, 1);
    this.reassignFrom(idx);
    return true;
  }

  /** 뷰를 맨 위로 이동 + 이동 구간 depth 재부여 — topmost 보장. 스택에 없으면 no-op. */
  bringToTop(view: V): void {
    const idx = this.entries.findIndex((e) => e.view === view);
    if (idx < 0 || idx === this.entries.length - 1) return;
    const [entry] = this.entries.splice(idx, 1);
    this.entries.push(entry);
    this.reassignFrom(idx);
  }

  /** tag 일치 뷰를 위에서부터 닫는다 — 스택 제거 + close 호출 (이벤트 remove와 중복 안전). */
  closeByTag(tag: string): void {
    this.closeWhere((e) => e.tag === tag);
  }

  /** 전체 닫기 — 위에서부터 (위 뷰가 아래 뷰에 의존하는 2단 팝업 역순 정리). */
  closeAll(): void {
    this.closeWhere(() => true);
  }

  private closeWhere(match: (entry: Entry<V>) => boolean): void {
    const targets = this.entries.filter(match).reverse();
    for (const t of targets) {
      this.remove(t.view);
      t.view.close();
    }
  }

  private reassignFrom(startIndex: number): void {
    for (let i = startIndex; i < this.entries.length; i++) {
      this.entries[i].view.setDepth(this.options.depthFor(i));
    }
  }
}
